/**
 * Geradores de mídia.
 * - Imagem: Gemini (Nano Banana / Imagen) ou OpenAI (gpt-image-1 / dall-e-3)
 * - Vídeo:  Higgsfield (Soul / DoP / Lite)
 *
 * Todas as chamadas vão DIRETO ao endpoint do provedor a partir do browser.
 * Funcionam em dev (Vite proxy não obrigatório), porém alguns provedores
 * podem rejeitar CORS conforme a região — nesses casos é necessário rodar
 * por trás de um proxy próprio. Estado de erro é reportado.
 */

export interface GenImageReq {
  apiKey: string;
  model: string;
  prompt: string;
  /** opcional: referência (foto-realismo, estilo, identidade) */
  referenceUrl?: string;
  /** "1:1" | "9:16" | "16:9" | "4:5" */
  aspect?: "1:1" | "9:16" | "16:9" | "4:5";
}

export interface GenImageResult {
  /** data URL (data:image/png;base64,…) — sempre embutido pra portabilidade */
  dataUrl: string;
  mime: string;
  provider: "gemini" | "openai";
  model: string;
}

export interface GenVideoReq {
  apiKey: string;
  model: string;
  prompt: string;
  /** url de imagem de referência (image-to-video) opcional */
  imageUrl?: string;
  /** segundos */
  duration?: number;
  aspect?: "9:16" | "16:9" | "1:1";
}

export interface GenVideoResult {
  url: string;            // url do vídeo gerado (mp4)
  provider: "higgsfield";
  model: string;
  jobId?: string;
}

/* ==================== GEMINI (Imagem) ==================== */

const GEMINI_ENDPOINT = (model: string, key: string) =>
  `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${encodeURIComponent(key)}`;

const GEMINI_FALLBACK_MODELS = [
  "gemini-3-pro-image-preview",
  "gemini-3.1-flash-image-preview",
  "gemini-2.5-flash-image"
];

async function callGeminiOnce(model: string, apiKey: string, parts: any[]): Promise<Response> {
  const body = {
    contents: [{ role: "user", parts }],
    generationConfig: { responseModalities: ["IMAGE", "TEXT"] }
  };
  return fetch(GEMINI_ENDPOINT(model, apiKey), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body)
  });
}

export async function generateImageGemini(req: GenImageReq): Promise<GenImageResult> {
  if (!req.apiKey) throw new Error("Sem chave Gemini. Configure em ⚙️.");

  const parts: any[] = [{ text: req.prompt }];
  if (req.referenceUrl) {
    const r = await fetch(req.referenceUrl);
    const buf = await r.arrayBuffer();
    const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
    parts.push({ inlineData: { mimeType: r.headers.get("content-type") ?? "image/jpeg", data: b64 } });
  }

  // tenta o modelo pedido; se 404 ou nome inválido, faz fallback
  const candidates = [req.model, ...GEMINI_FALLBACK_MODELS.filter(m => m !== req.model)];
  let lastError = "";
  for (const model of candidates) {
    const res = await callGeminiOnce(model, req.apiKey, parts);
    if (res.ok) {
      const json = await res.json();
      const cand = json?.candidates?.[0];
      const imgPart = cand?.content?.parts?.find((p: any) => p.inlineData?.data);
      if (!imgPart) {
        lastError = `${model}: sem imagem na resposta`;
        continue;
      }
      const mime = imgPart.inlineData.mimeType || "image/png";
      return {
        dataUrl: `data:${mime};base64,${imgPart.inlineData.data}`,
        mime, provider: "gemini", model
      };
    } else {
      const txt = await res.text();
      lastError = `${model} → ${res.status}: ${txt.slice(0, 150)}`;
      // só faz fallback em 404 ou modelo não encontrado
      if (res.status !== 404 && !/not found|not supported/i.test(txt)) {
        throw new Error(`Gemini ${res.status}: ${txt.slice(0, 300)}`);
      }
    }
  }
  throw new Error(`Gemini falhou em todos os modelos. Último: ${lastError}`);
}

/* ==================== OPENAI (Imagem) ==================== */

const OPENAI_IMG_ENDPOINT = "https://api.openai.com/v1/images/generations";

const OPENAI_SIZE = (aspect?: GenImageReq["aspect"]) => {
  switch (aspect) {
    case "9:16": return "1024x1792";
    case "16:9": return "1792x1024";
    case "4:5":  return "1024x1280";
    default:     return "1024x1024";
  }
};

export async function generateImageOpenAI(req: GenImageReq): Promise<GenImageResult> {
  if (!req.apiKey) throw new Error("Sem chave OpenAI. Configure em ⚙️.");

  const body: any = {
    model: req.model || "gpt-image-1",
    prompt: req.prompt,
    size: OPENAI_SIZE(req.aspect),
    n: 1
  };
  // gpt-image-1 retorna base64 por padrão; dall-e-3 retorna URL.
  if (req.model === "gpt-image-1") body.response_format = "b64_json";
  else body.response_format = "b64_json";

  const res = await fetch(OPENAI_IMG_ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "authorization": `Bearer ${req.apiKey}`
    },
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`OpenAI ${res.status}: ${t.slice(0, 300)}`);
  }
  const json = await res.json();
  const b64 = json?.data?.[0]?.b64_json;
  const url = json?.data?.[0]?.url;
  if (b64) return { dataUrl: `data:image/png;base64,${b64}`, mime: "image/png", provider: "openai", model: req.model };
  if (url) {
    const r = await fetch(url);
    const buf = await r.arrayBuffer();
    const b = btoa(String.fromCharCode(...new Uint8Array(buf)));
    return { dataUrl: `data:image/png;base64,${b}`, mime: "image/png", provider: "openai", model: req.model };
  }
  throw new Error("OpenAI não retornou imagem.");
}

/* ==================== HIGGSFIELD (Vídeo) ==================== */

const HIGGS_ENDPOINT = "https://platform.higgsfield.ai/v1/generations/video";
const HIGGS_STATUS = (jobId: string) => `https://platform.higgsfield.ai/v1/generations/${jobId}`;

export async function generateVideoHiggsfield(req: GenVideoReq): Promise<GenVideoResult> {
  if (!req.apiKey) throw new Error("Sem chave Higgsfield. Configure em ⚙️.");

  const payload: any = {
    model: req.model || "higgsfield-soul",
    prompt: req.prompt,
    duration: req.duration ?? 5,
    aspect_ratio: req.aspect ?? "9:16"
  };
  if (req.imageUrl) payload.image_url = req.imageUrl;

  const start = await fetch(HIGGS_ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "authorization": `Bearer ${req.apiKey}`
    },
    body: JSON.stringify(payload)
  });
  if (!start.ok) {
    const t = await start.text();
    throw new Error(`Higgsfield ${start.status}: ${t.slice(0, 300)}`);
  }
  const startJson = await start.json();
  const jobId = startJson?.id ?? startJson?.job_id ?? startJson?.generation_id;
  if (!jobId) throw new Error("Higgsfield não retornou job id. " + JSON.stringify(startJson).slice(0, 200));

  // polling até concluir
  const deadline = Date.now() + 5 * 60 * 1000;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 3000));
    const st = await fetch(HIGGS_STATUS(jobId), {
      headers: { "authorization": `Bearer ${req.apiKey}` }
    });
    if (!st.ok) continue;
    const sj = await st.json();
    const status = sj?.status ?? sj?.state;
    const videoUrl = sj?.video_url ?? sj?.output?.video_url ?? sj?.result?.url;
    if (status === "completed" || status === "succeeded" || videoUrl) {
      if (videoUrl) return { url: videoUrl, provider: "higgsfield", model: payload.model, jobId };
    }
    if (status === "failed" || status === "error") {
      throw new Error("Higgsfield falhou: " + JSON.stringify(sj).slice(0, 200));
    }
  }
  throw new Error("Higgsfield timeout (5min). Job " + jobId);
}

/* ==================== HEURÍSTICA DE CAPACIDADE ==================== */

/** Detecta se uma persona é capaz de gerar imagem/vídeo com base em skills/dept. */
export type Capability = "image" | "video" | "copy" | "strategy";

export function capabilitiesOf(slotId: string, skills: string[]): Capability[] {
  const [dept] = slotId.split(":");
  const set = new Set<Capability>();
  const skillStr = skills.join(" ").toLowerCase();

  if (dept === "criativo") set.add("image");
  if (dept === "video")    set.add("video");
  if (dept === "copy")     set.add("copy");

  if (/imagem|design|carrossel|branding|identidade|tipografia|3d|motion/.test(skillStr)) set.add("image");
  if (/vídeo|video|edição|cinematografia|motion|trailer|vsl|filmaker/.test(skillStr))    set.add("video");
  if (/copy|headline|carta|VSL|funil|narrativa/.test(skillStr))                            set.add("copy");
  if (/estratégia|negociação|gestão|conselho|métrica|tráfego|análise/.test(skillStr))     set.add("strategy");

  return [...set];
}
